import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack, TextField, Typography } from '@mui/material';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { IExchangeRateMarketAverage, IReportPeriodExchangeRate } from '../../../../shared/services/report-period/report-period.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

export interface ICurrencyOption {
  code: string;
  symbol?: string | null;
}

interface ExchangeRateDialogProps {
  open: boolean;
  reportPeriodId: string;
  currencyOptions: ICurrencyOption[];
  // The rate being edited; absent when adding.
  editRate?: IReportPeriodExchangeRate | null;
  onClose: () => void;
  onSaved: () => void;
}

// A typed rate more than this far from the period's market average gets a warning (never a block).
const MARKET_AVERAGE_WARNING_THRESHOLD = 0.1;

const formatRate = (value: number) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 }).format(value);

// The market average is a sanity figure, so four decimals are enough and read more easily.
const formatAverage = (value: number) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4 }).format(value);

export const ExchangeRateDialog: React.FC<ExchangeRateDialogProps> = ({
  open,
  reportPeriodId,
  currencyOptions,
  editRate,
  onClose,
  onSaved,
}) => {
  const reportPeriodService = new ReportPeriodService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isEdit = Boolean(editRate);

  const [fromCurrencyCode, setFromCurrencyCode] = useState<string>('');
  const [toCurrencyCode, setToCurrencyCode] = useState<string>('');
  const [rateInput, setRateInput] = useState<string>('');
  const [rateSource, setRateSource] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [marketAverage, setMarketAverage] = useState<IExchangeRateMarketAverage | null>(null);

  // Seed on open; reset on close, so a reopened dialog never shows the last rate typed.
  useEffect(() => {
    if (open) {
      setFromCurrencyCode(editRate?.fromCurrencyCode ?? '');
      setToCurrencyCode(editRate?.toCurrencyCode ?? '');
      setRateInput(editRate ? String(editRate.rate) : '');
      setRateSource(editRate?.rateSource ?? '');
      setErrorMessage('');
    } else {
      setMarketAverage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editRate?.id]);

  // The market average for the pair, fetched once a valid pair is chosen. A failed or uncovered
  // lookup simply shows nothing; it never blocks a save.
  useEffect(() => {
    setMarketAverage(null);
    if (!open || !reportPeriodId || !fromCurrencyCode || !toCurrencyCode || fromCurrencyCode === toCurrencyCode) {
      return;
    }
    let cancelled = false;
    reportPeriodService.getExchangeRateMarketAverage(reportPeriodId, fromCurrencyCode, toCurrencyCode)
      .then((response) => {
        if (!cancelled) setMarketAverage(response);
      })
      .catch(() => {
        if (!cancelled) setMarketAverage(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportPeriodId, fromCurrencyCode, toCurrencyCode]);

  const rate = rateInput.trim() === '' ? NaN : Number(rateInput);
  const rateIsValid = Number.isFinite(rate) && rate > 0;
  const pairIsValid = !!fromCurrencyCode && !!toCurrencyCode && fromCurrencyCode !== toCurrencyCode;
  const sourceIsValid = rateSource.trim() !== '';

  const deviation = useMemo(() => {
    if (!rateIsValid || !marketAverage?.average) return null;
    return Math.abs(rate - marketAverage.average) / marketAverage.average;
  }, [rate, rateIsValid, marketAverage]);

  const handleClose = () => {
    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!pairIsValid) {
      setErrorMessage('Choose two different currencies');
      return;
    }
    if (!rateIsValid) {
      setErrorMessage('The rate must be a number greater than zero');
      return;
    }
    if (!sourceIsValid) {
      setErrorMessage('Enter where the rate came from');
      return;
    }
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      if (isEdit && editRate) {
        await reportPeriodService.updateExchangeRate(reportPeriodId, editRate.id, { rate, rateSource: rateSource.trim() });
      } else {
        await reportPeriodService.createExchangeRate(reportPeriodId, {
          fromCurrencyCode,
          toCurrencyCode,
          rate,
          rateSource: rateSource.trim(),
        });
      }
      userNotificationsContext.openSnackbar({ open: true, message: isEdit ? 'Fixed rate updated' : 'Fixed rate saved' });
      onSaved();
      handleClose();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'The exchange rate could not be saved, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const optionLabel = (code: string) => {
    const option = currencyOptions.find((o) => o.code === code);
    return option?.symbol ? `${code} (${option.symbol})` : code;
  };
  const codes = currencyOptions.map((o) => o.code);

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit Fixed Rate' : 'Add Fixed Rate'}
      cancelHandler={handleClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      maxWidth='sm'
    >
      <Stack sx={{ gap: 2, pt: 1 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Every spend in this reporting period that converts between these two currencies uses this rate instead of the daily market rate. Enter it as 1 unit of the first currency.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 2 }}>
          <FormControl fullWidth>
            <Autocomplete
              options={codes}
              autoHighlight
              disabled={isEdit || isSubmitting}
              value={fromCurrencyCode || null}
              getOptionLabel={optionLabel}
              onChange={(_, value) => {
                setFromCurrencyCode(value ?? '');
                setErrorMessage('');
              }}
              renderInput={(params) => <TextField {...params} label='From currency' />}
            />
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              options={codes}
              autoHighlight
              disabled={isEdit || isSubmitting}
              value={toCurrencyCode || null}
              getOptionLabel={optionLabel}
              onChange={(_, value) => {
                setToCurrencyCode(value ?? '');
                setErrorMessage('');
              }}
              renderInput={(params) => <TextField {...params} label='To currency' />}
            />
          </FormControl>
        </Stack>
        {fromCurrencyCode && toCurrencyCode && fromCurrencyCode === toCurrencyCode && (
          <Typography variant='body2' color='error'>Choose two different currencies</Typography>
        )}

        <FormControl fullWidth>
          <InputLabel htmlFor='exchange-rate-value'>Rate</InputLabel>
          <OutlinedInput
            id='exchange-rate-value'
            label='Rate'
            type='number'
            inputProps={{ min: 0, step: 'any' }}
            value={rateInput}
            disabled={isSubmitting}
            onChange={(e) => {
              setRateInput(e.target.value);
              setErrorMessage('');
            }}
          />
          <FormHelperText>
            {pairIsValid && rateIsValid
              ? `1 ${fromCurrencyCode} = ${formatRate(rate)} ${toCurrencyCode}`
              : 'How many units of the second currency one unit of the first buys'}
          </FormHelperText>
        </FormControl>

        {pairIsValid && marketAverage?.average != null && (
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: -1 }}>
            Market average for this period: 1 {fromCurrencyCode} = {formatAverage(marketAverage.average)} {toCurrencyCode}
          </Typography>
        )}
        {pairIsValid && marketAverage?.average != null && deviation != null && deviation > MARKET_AVERAGE_WARNING_THRESHOLD && (
          <Alert severity='warning'>
            This is {Math.round(deviation * 100)}% away from the market average for the period ({formatAverage(marketAverage.average)}). Check the direction: 1 {fromCurrencyCode} = {formatRate(rate)} {toCurrencyCode}.
          </Alert>
        )}

        <FormControl fullWidth>
          <InputLabel htmlFor='exchange-rate-source'>Where the rate came from</InputLabel>
          <OutlinedInput
            id='exchange-rate-source'
            label='Where the rate came from'
            value={rateSource}
            disabled={isSubmitting}
            inputProps={{ maxLength: 255 }}
            onChange={(e) => {
              setRateSource(e.target.value);
              setErrorMessage('');
            }}
          />
          <FormHelperText>For example HMRC yearly average 2021, or your group treasury rate</FormHelperText>
        </FormControl>

        {errorMessage && (
          <Box>
            <Alert severity='error'>{errorMessage}</Alert>
          </Box>
        )}
      </Stack>
    </DialogWrapper>
  );
};
